import i18n from "@i18n/i18n";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";

export interface EditBenchmarkModelTypeDialogProps {
  open: boolean;
  entityId: string;
  currentName: string;
  existingNames: string[];
  onClose: () => void;
  onSave: (name: string) => Promise<void>;
  isSaving: boolean;
  confirmMessage?: string;
}

export default function EditBenchmarkModelTypeDialog({
  open,
  entityId: _entityId,
  currentName,
  existingNames,
  onClose,
  onSave,
  isSaving,
  confirmMessage,
}: EditBenchmarkModelTypeDialogProps) {
  const [name, setName] = useState(currentName);
  const [duplicateError, setDuplicateError] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  useEffect(() => {
    if (open) {
      setName(currentName);
      setDuplicateError(false);
      setShowConfirm(false);
    }
  }, [open, currentName]);

  const normalizedExisting = existingNames.map((n) => n.trim().toLowerCase());

  const handleSave = async () => {
    const trimmed = name.trim();
    if (!trimmed) return;

    const isDuplicate =
      trimmed.toLowerCase() !== currentName.trim().toLowerCase() &&
      normalizedExisting.includes(trimmed.toLowerCase());
    if (isDuplicate) {
      setDuplicateError(true);
      return;
    }
    setDuplicateError(false);

    if (confirmMessage && trimmed.toLowerCase() !== currentName.trim().toLowerCase()) {
      setShowConfirm(true);
      return;
    }

    await onSave(trimmed);
    onClose();
  };

  const handleConfirmProceed = async () => {
    setShowConfirm(false);
    await onSave(name.trim());
    onClose();
  };

  const handleClose = () => {
    setName(currentName);
    setDuplicateError(false);
    setShowConfirm(false);
    onClose();
  };

  return (
    <>
      <Dialog open={open && !showConfirm} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          {i18n.t("rebar-material-grid.edit-benchmark-model-type-dialog.title")}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label={i18n.t("rebar-material-grid.edit-benchmark-model-type-dialog.name-label")}
            placeholder={i18n.t("rebar-material-grid.edit-benchmark-model-type-dialog.name-placeholder")}
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              setDuplicateError(false);
            }}
            fullWidth
            variant="outlined"
            error={duplicateError}
            disabled={isSaving}
          />
          {duplicateError && (
            <Typography color="error" variant="body2" sx={{ mt: 1 }}>
              {i18n.t("rebar-material-grid.edit-benchmark-model-type-dialog.validation.duplicate")}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={isSaving}>
            {i18n.t("rebar-material-grid.edit-benchmark-model-type-dialog.cancel")}
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={isSaving || !name.trim()}
          >
            {isSaving
              ? i18n.t("rebar-material-grid.edit-benchmark-model-type-dialog.saving")
              : i18n.t("rebar-material-grid.edit-benchmark-model-type-dialog.save")}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={showConfirm} onClose={() => setShowConfirm(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{i18n.t("rebar-material-grid.edit-benchmark-model-type-dialog.confirm-title")}</DialogTitle>
        <DialogContent>
          <DialogContentText>{confirmMessage}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowConfirm(false)} variant="outlined">
            {i18n.t("rebar-material-grid.edit-benchmark-model-type-dialog.cancel")}
          </Button>
          <Button onClick={handleConfirmProceed} variant="contained" loading={isSaving}>
            {i18n.t("rebar-material-grid.edit-benchmark-model-type-dialog.confirm-proceed")}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
